/**
 * Stand-in documents for the tour steps that open a letter, a statement or an
 * export.
 *
 * A tour must never download a real document, so the "files" it shows are built
 * here as `data:` URIs — the PDF bytes are assembled in the browser and handed
 * straight to the viewer, so nothing is requested and nothing production ever
 * leaves the tenant.
 */

/**
 * Make a caption safe to write into a PDF string literal.
 *
 * Besides escaping the delimiters, this folds the text down to ASCII. The
 * assembled file is base64-encoded with `btoa`, which throws on any code point
 * above 0xFF — so a caption carrying a dash, a curly quote or a non-Latin name
 * would otherwise break the document it is meant to title.
 */
const pdfText = (value: string): string =>
  value
    .replaceAll('\u2014', '-')
    .replaceAll('\u2013', '-')
    .replaceAll(/[\u2018\u2019]/gu, "'")
    .replaceAll(/[\u201C\u201D]/gu, '"')
    // Anything still outside printable ASCII cannot be encoded below.
    .replaceAll(/[^\u0020-\u007E]/gu, '?')
    .replaceAll('\\', String.raw`\\`)
    .replaceAll('(', String.raw`\(`)
    .replaceAll(')', String.raw`\)`);

/**
 * Assemble a one-page PDF with the given lines of text and return it as a
 * `data:` URI. Offsets in the cross-reference table are computed from the
 * bytes actually written, so the file is valid rather than merely plausible.
 */
export const buildTourPdfDataUri = (title: string, lines: string[]): string => {
  const content = [
    'BT',
    '/F1 18 Tf',
    '72 760 Td',
    `(${pdfText(title)}) Tj`,
    '/F1 11 Tf',
    ...lines.flatMap(line => ['0 -26 Td', `(${pdfText(line)}) Tj`]),
    'ET',
  ].join('\n');

  const objects = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] ' +
      '/Resources << /Font << /F1 5 0 R >> >> /Contents 4 0 R >>',
    `<< /Length ${content.length} >>\nstream\n${content}\nendstream`,
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>',
  ];

  let body = '%PDF-1.4\n';
  const offsets: number[] = [];
  objects.forEach((object, index) => {
    offsets.push(body.length);
    body += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });

  const xrefOffset = body.length;
  const xref = [
    'xref',
    `0 ${objects.length + 1}`,
    '0000000000 65535 f ',
    ...offsets.map(offset => `${String(offset).padStart(10, '0')} 00000 n `),
  ].join('\n');

  const pdf =
    `${body}${xref}\ntrailer\n` +
    `<< /Size ${objects.length + 1} /Root 1 0 R >>\n` +
    `startxref\n${xrefOffset}\n%%EOF`;

  // btoa needs latin1. Captions are folded to ASCII by pdfText above and the
  // rest of the file is ASCII syntax, so nothing here can exceed 0xFF.
  return `data:application/pdf;base64,${btoa(pdf)}`;
};

const DISCLAIMER =
  'Sample document - Help & Tours demo data. Not a real award.';

/** A generic stand-in for template previews and document downloads. */
export const tourGenericDocumentPdf = (title: string): string =>
  buildTourPdfDataUri(title, [
    'This document is generated locally for the Help & Tours walkthrough.',
    '',
    DISCLAIMER,
  ]);

/**
 * The same demo document as a blob object URL.
 *
 * Some screens hand the document URL straight to `window.open` rather than to
 * a viewer, and browsers refuse to open a `data:` URI as a top-level
 * navigation — so those endpoints need a blob URL instead. URLs are cached by
 * title so repeating a step does not pile up object URLs, and they live as
 * long as the page does (the tour ends well before that matters).
 */
const objectUrlCache = new Map<string, string>();

export const tourDocumentObjectUrl = (title: string): string => {
  const cached = objectUrlCache.get(title);
  if (cached) return cached;

  const dataUri = tourGenericDocumentPdf(title);
  // Object URLs are absent outside a browser (jsdom, SSR). The data URI still
  // renders in a viewer, so fall back to it rather than failing the step.
  if (typeof URL?.createObjectURL !== 'function') return dataUri;

  const bytes = Uint8Array.from(
    atob(dataUri.split(',')[1] ?? ''),
    character => character.codePointAt(0) ?? 0
  );
  const url = URL.createObjectURL(
    new Blob([bytes], { type: 'application/pdf' })
  );
  objectUrlCache.set(title, url);
  return url;
};

/** Release the cached object URLs. Called when a tour ends. */
export const releaseTourDocumentUrls = () => {
  if (typeof URL?.revokeObjectURL === 'function') {
    objectUrlCache.forEach(url => URL.revokeObjectURL(url));
  }
  objectUrlCache.clear();
};

/* -------------------------------------------------------------------------- */
/* Letter previews                                                            */
/* -------------------------------------------------------------------------- */

/**
 * A letter as the preview screens consume it.
 *
 * Despite its name, `PdfViewer` does not render a PDF — it passes `subject` and
 * `bodyContent` to `LetterContent`, which writes the body into the page as
 * HTML. The award-letter endpoints therefore return this object, not a file;
 * handing those screens a document URL leaves the letter area blank.
 */
export interface TourLetterContent {
  subject: string;
  bodyContent: string;
  templateName: string;
  templateId: number;
}

/** Shared letter shell, so every sample letter reads like the same template. */
const letterHtml = (
  salutation: string,
  paragraphs: string[],
  rows: [string, string][]
): string =>
  [
    `<p>Dear ${salutation},</p>`,
    ...paragraphs.map(text => `<p>${text}</p>`),
    '<table style="width:100%;border-collapse:collapse;margin:16px 0;">',
    '<tbody>',
    ...rows.map(
      ([label, value]) =>
        `<tr>` +
        // Horizontal padding on both cells: the letter viewer draws cell
        // borders, so unpadded text sat against the lines.
        `<td style="padding:6px 12px;width:45%;"><strong>${label}</strong></td>` +
        `<td style="padding:6px 12px;">${value}</td>` +
        `</tr>`
    ),
    '</tbody>',
    '</table>',
    '<p>This letter is issued for the Help &amp; Tours walkthrough and uses sample data. ' +
      'It does not represent a real award.</p>',
    '<p>Yours sincerely,<br/><strong>Northwind Share Plan Administration</strong></p>',
  ].join('');

/** The contingent award letter shown on the award-response screens. */
export const tourAwardLetterContent = (award: {
  employeeName: string;
  employeeId: string;
  awardNo: string;
  planName: string;
  awardDateDisplay: string;
  awardedShares: number;
  performancePeriod: string;
  expiryDate: string;
}): TourLetterContent => ({
  subject: `Your ${award.planName} award - ${award.awardNo}`,
  templateName: 'PSP Contingent Award Letter',
  templateId: 4101,
  bodyContent: letterHtml(
    award.employeeName,
    [
      'We are pleased to confirm that you have been granted a contingent award ' +
        `under the <strong>${award.planName}</strong>.`,
      'The award is subject to your continued employment and to the performance ' +
        'conditions set out in the plan rules. The number of shares that ' +
        'ultimately vests may be higher or lower than the contingent amount ' +
        'shown below.',
    ],
    [
      ['Award Number', award.awardNo],
      ['Employee', `${award.employeeName} (${award.employeeId})`],
      ['Grant Date', award.awardDateDisplay],
      ['Contingent Shares', award.awardedShares.toLocaleString('en-SG')],
      ['Performance Period', award.performancePeriod],
      ['Response Due By', award.expiryDate],
    ]
  ),
});

/** The final award letter shown once an award has vested. */
export const tourFinalAwardLetterContent = (award: {
  employeeName: string;
  employeeId: string;
  awardNo: string;
  planName: string;
  contingentShares: number;
  achievementFactor: number;
  finalShares: number;
  vestingDate: string;
}): TourLetterContent => ({
  subject: `Vesting of your ${award.planName} award - ${award.awardNo}`,
  templateName: 'Final Award Vesting Letter',
  templateId: 4102,
  bodyContent: letterHtml(
    award.employeeName,
    [
      `Your award under the <strong>${award.planName}</strong> has now vested ` +
        'following the close of the performance period.',
      'The shares set out below will be credited to your securities account in ' +
        'the next crediting run.',
    ],
    [
      ['Award Number', award.awardNo],
      ['Employee', `${award.employeeName} (${award.employeeId})`],
      ['Contingent Shares', award.contingentShares.toLocaleString('en-SG')],
      ['Achievement Factor', award.achievementFactor.toFixed(2)],
      ['Final Vested Shares', award.finalShares.toLocaleString('en-SG')],
      ['Vesting Date', award.vestingDate],
    ]
  ),
});
